import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

export default function ShareContactModal({
  open,
  setOpen,
  cardHolderEmail,
  cardHolderName,
}) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [success, setSuccess] = useState(false);
  const cancelButtonRef = useRef(null);
  const isSubmittingRef = useRef(false); // Prevent double submission

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmittingRef.current || loading) {
      return;
    }

    // Validation
    if (!name || !email) {
      alert("Please fill in at least your name and email");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert("Please enter a valid email address");
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // Get location (optional - won't block if denied)
      let location = null;
      try {
        if (navigator.geolocation) {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
              enableHighAccuracy: false,
            });
          });

          // Reverse geocode to get city name
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;

          try {
            const geoResponse = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
            );
            const geoData = await geoResponse.json();

            location = {
              city:
                geoData.address?.city ||
                geoData.address?.town ||
                geoData.address?.village ||
                "Unknown",
              state: geoData.address?.state || "",
              country: geoData.address?.country || "",
              latitude: lat,
              longitude: lon,
              timestamp: new Date().toISOString(),
            };
          } catch (geoApiError) {
            // If reverse geocoding fails, just use coordinates
            location = {
              city: "Unknown",
              latitude: lat,
              longitude: lon,
              timestamp: new Date().toISOString(),
            };
          }
        }
      } catch (geoError) {
        // Location not available, continue without location
      }

      const contact = {
        name,
        email,
        phone: phone || "",
        exchanged: true,
        timestamp: new Date().toISOString(),
        location: location,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}add_contact`,
        {
          email: cardHolderEmail,
          contact: contact,
        }
      );

      if (response.status === 200) {
        setSuccess(true);
        // Reset form
        setTimeout(() => {
          setName("");
          setEmail("");
          setPhone("");
          setSuccess(false);
          setOpen(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Error sharing contact:", error);
      alert("Failed to share contact. Please try again.");
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                {success ? (
                  <div className="text-center py-8">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key="success"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 200,
                          damping: 20,
                        }}
                        className="mb-6"
                      >
                        {/* Checkmark Circle */}
                        <div className="relative inline-flex items-center justify-center">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              delay: 0.1,
                              type: "spring",
                              stiffness: 200,
                              damping: 15,
                            }}
                            className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center"
                          >
                            <motion.svg
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{
                                delay: 0.2,
                                duration: 0.4,
                                ease: "easeOut",
                              }}
                              className="w-12 h-12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#22c55e"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <motion.path d="M20 6L9 17l-5-5" />
                            </motion.svg>
                          </motion.div>
                        </div>
                      </motion.div>
                    </AnimatePresence>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold leading-6 text-gray-900"
                      >
                        Contact Shared!
                      </Dialog.Title>
                      <p className="mt-3 text-sm text-gray-500">
                        {cardHolderName} will receive your contact information.
                      </p>
                    </motion.div>
                  </div>
                ) : loading ? (
                  <div className="text-center py-12">
                    {/* Loading Spinner */}
                    <motion.div
                      className="inline-block"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <svg
                        className="w-16 h-16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <motion.circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray="60"
                          strokeDashoffset="15"
                        />
                      </svg>
                    </motion.div>
                    <p className="mt-4 text-sm text-gray-500">
                      Sharing your contact...
                    </p>
                  </div>
                ) : (
                  <>
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Share Your Contact
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-gray-500">
                        Exchange contact info with {cardHolderName}
                      </p>
                    </motion.div>

                    <motion.div
                      className="mt-6 space-y-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your full name"
                          autoComplete="name"
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your.email@example.com"
                          autoComplete="email"
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Phone{" "}
                          <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          autoComplete="tel"
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </motion.div>

                    <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                      <motion.button
                        type="button"
                        disabled={loading}
                        className="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                        onClick={handleSubmit}
                        whileTap={{ scale: 0.95 }}
                        whileHover={{ scale: 1.02 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 17,
                        }}
                      >
                        {loading ? "Sharing..." : "Share Contact"}
                      </motion.button>
                      <motion.button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                        onClick={() => setOpen(false)}
                        ref={cancelButtonRef}
                        whileTap={{ scale: 0.95 }}
                        whileHover={{ scale: 1.02 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 17,
                        }}
                      >
                        Cancel
                      </motion.button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
